<template>
  <div class="home-page space-y-5">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Dashboard</h1>
      <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
        Welcome back, {{ userName }}
      </p>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 mb-1">Orders Today</p>
        <div v-if="statsLoading" class="h-8 w-12 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" aria-hidden="true"></div>
        <p v-else-if="statsError" class="text-2xl font-bold text-neutral-400 dark:text-neutral-500" title="Couldn't load">&mdash;</p>
        <p v-else class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{{ stats.todayOrders }}</p>
      </div>
      <div class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 mb-1">Pending</p>
        <div v-if="statsLoading" class="h-8 w-12 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" aria-hidden="true"></div>
        <p v-else-if="statsError" class="text-2xl font-bold text-neutral-400 dark:text-neutral-500" title="Couldn't load">&mdash;</p>
        <p v-else class="text-2xl font-bold text-amber-600 dark:text-amber-400 tabular-nums">{{ stats.pendingOrders }}</p>
      </div>
      <div class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 mb-1">Routes</p>
        <div v-if="statsLoading" class="h-8 w-12 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" aria-hidden="true"></div>
        <p v-else-if="statsError" class="text-2xl font-bold text-neutral-400 dark:text-neutral-500" title="Couldn't load">&mdash;</p>
        <p v-else class="text-2xl font-bold text-secondary-600 dark:text-secondary-400 tabular-nums">{{ stats.todayRoutes }}</p>
      </div>
      <div class="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
        <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 mb-1">Suppliers</p>
        <div v-if="suppliersLoading" class="h-8 w-12 rounded bg-neutral-200 dark:bg-neutral-700 animate-pulse" aria-hidden="true"></div>
        <p v-else-if="suppliersError" class="text-2xl font-bold text-neutral-400 dark:text-neutral-500" title="Couldn't load">&mdash;</p>
        <p v-else class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{{ stats.totalSuppliers }}</p>
      </div>
    </div>

    <!-- Stats fetch failure: a zero must never masquerade as "no orders" -->
    <div v-if="(statsError || suppliersError) && !statsLoading" class="flex items-center gap-2 text-sm">
      <span class="material-icons text-[18px] text-amber-500" aria-hidden="true">warning</span>
      <span class="text-neutral-600 dark:text-neutral-300">Couldn't load dashboard stats.</span>
      <button @click="fetchStats" class="font-semibold text-primary-700 dark:text-primary-400 hover:underline">
        Retry
      </button>
    </div>

    <!-- Quick Navigation -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <!-- Operations -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Operations</h2>
        </div>
        <div class="p-2">
          <router-link v-for="item in opsLinks" :key="item.route" :to="item.route"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" :class="item.bg">
              <span class="material-icons text-[18px]" :class="item.iconColor" aria-hidden="true">{{ item.icon }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{{ item.label }}</p>
              <p class="text-[11px] text-neutral-500 dark:text-neutral-400">{{ item.desc }}</p>
            </div>
            <span class="material-icons text-neutral-300 dark:text-neutral-600 text-lg" aria-hidden="true">chevron_right</span>
          </router-link>
        </div>
      </div>

      <!-- HomeBase -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">HomeBase</h2>
        </div>
        <div class="p-2">
          <router-link v-for="item in homebaseLinks" :key="item.route" :to="item.route"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" :class="item.bg">
              <span class="material-icons text-[18px]" :class="item.iconColor" aria-hidden="true">{{ item.icon }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{{ item.label }}</p>
              <p class="text-[11px] text-neutral-500 dark:text-neutral-400">{{ item.desc }}</p>
            </div>
            <span class="material-icons text-neutral-300 dark:text-neutral-600 text-lg" aria-hidden="true">chevron_right</span>
          </router-link>
        </div>
      </div>

      <!-- Administration -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Administration</h2>
        </div>
        <div class="p-2">
          <router-link v-for="item in adminLinks" :key="item.route" :to="item.route"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" :class="item.bg">
              <span class="material-icons text-[18px]" :class="item.iconColor" aria-hidden="true">{{ item.icon }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{{ item.label }}</p>
              <p class="text-[11px] text-neutral-500 dark:text-neutral-400">{{ item.desc }}</p>
            </div>
            <span class="material-icons text-neutral-300 dark:text-neutral-600 text-lg" aria-hidden="true">chevron_right</span>
          </router-link>
        </div>
      </div>

      <!-- Settings -->
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div class="px-5 py-3.5 border-b border-neutral-100 dark:border-neutral-700">
          <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Settings</h2>
        </div>
        <div class="p-2">
          <router-link v-for="item in settingsLinks" :key="item.route" :to="item.route"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group">
            <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" :class="item.bg">
              <span class="material-icons text-[18px]" :class="item.iconColor" aria-hidden="true">{{ item.icon }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{{ item.label }}</p>
              <p class="text-[11px] text-neutral-500 dark:text-neutral-400">{{ item.desc }}</p>
            </div>
            <span class="material-icons text-neutral-300 dark:text-neutral-600 text-lg" aria-hidden="true">chevron_right</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { dubaiTodayStr } from "@/utils/dubaiDay";
import { useStore } from "vuex";
import axios from "axios";
import { collection, onSnapshot, query, where } from "@firebase/firestore";
import { db } from "@/firebase/config";

export default {
  name: "Home",
  setup() {
    const store = useStore();
    const apiUrl = store.state.apiUrl;

    const userName = computed(() => store.state.user?.displayName || "");

    const stats = ref({
      todayOrders: 0,
      pendingOrders: 0,
      todayRoutes: 0,
      totalSuppliers: 0,
    });

    // On an ops dashboard a silent 0 is indistinguishable from "no orders",
    // so loading shows a skeleton and failures show an em-dash + retry.
    const statsLoading = ref(true);
    const statsError = ref(false);
    const suppliersLoading = ref(true);
    const suppliersError = ref(false);

    // Fetch today's orders + routes
    const fetchStats = async () => {
      statsLoading.value = true;
      let failed = false;

      try {
        const today = new Date();
        const payload = {
          start_date: today.toISOString(),
          site_id: store.state.currentSite,
        };
        const ordersRes = await axios.post(`${apiUrl}/getOrders`, payload);
        const orders = ordersRes.data || [];
        stats.value.todayOrders = orders.length;
        stats.value.pendingOrders = orders.filter(
          (o) => o.status === "REQUESTED" || o.status === "PLANNED"
        ).length;
      } catch (e) {
        failed = true;
      }

      try {
        const routesRes = await axios.get(
          `${apiUrl}/getPlannedRoutes/${store.state.currentSite}`,
          { params: { scheduledDate: dubaiTodayStr() } }
        );
        stats.value.todayRoutes = (routesRes.data || []).length;
      } catch (e) {
        failed = true;
      }

      statsError.value = failed;
      statsLoading.value = false;
    };

    // Supplier count from Firestore — filtered server-side and unsubscribed on
    // unmount (the listener used to read every site's suppliers and leak per visit).
    let unsubSuppliers = null;

    onMounted(() => {
      fetchStats();
      const colRef = query(
        collection(db, "suppliers"),
        where("siteid", "==", store.state.currentSite)
      );
      unsubSuppliers = onSnapshot(
        colRef,
        (snapshot) => {
          stats.value.totalSuppliers = snapshot.size;
          suppliersLoading.value = false;
          suppliersError.value = false;
        },
        () => {
          suppliersLoading.value = false;
          suppliersError.value = true;
        }
      );
    });

    onUnmounted(() => {
      if (unsubSuppliers) unsubSuppliers();
    });

    const opsLinks = [
      { route: "/dashboard/operations/orders", icon: "shopping_cart", label: "Orders", desc: "Manage collection orders", bg: "bg-primary-50 dark:bg-primary-900/20", iconColor: "text-primary-600 dark:text-primary-400" },
      { route: "/dashboard/operations/planning", icon: "route", label: "Route Planning", desc: "Plan and view routes", bg: "bg-secondary-50 dark:bg-secondary-900/20", iconColor: "text-secondary-600 dark:text-secondary-400" },
      { route: "/dashboard/settings/zones", icon: "location_on", label: "Zone Planning", desc: "Configure zone schedules", bg: "bg-violet-50 dark:bg-violet-900/20", iconColor: "text-violet-600 dark:text-violet-400" },
    ];

    const homebaseLinks = [
      { route: "/dashboard/operations/homebase/signups", icon: "person_add", label: "Signups", desc: "Manage signups", bg: "bg-blue-50 dark:bg-blue-900/20", iconColor: "text-blue-600 dark:text-blue-400" },
      { route: "/dashboard/operations/homebase/deployments", icon: "local_shipping", label: "Deployments", desc: "Track deployments", bg: "bg-teal-50 dark:bg-teal-900/20", iconColor: "text-teal-600 dark:text-teal-400" },
      { route: "/dashboard/operations/homebase/households", icon: "groups", label: "Households", desc: "Manage households", bg: "bg-pink-50 dark:bg-pink-900/20", iconColor: "text-pink-600 dark:text-pink-400" },
      { route: "/dashboard/operations/homebase/containers", icon: "inventory_2", label: "Containers", desc: "Track containers", bg: "bg-amber-50 dark:bg-amber-900/20", iconColor: "text-amber-600 dark:text-amber-400" },
      { route: "/dashboard/operations/homebase/collection-requests", icon: "assignment", label: "Collection Requests", desc: "Manage requests", bg: "bg-cyan-50 dark:bg-cyan-900/20", iconColor: "text-cyan-600 dark:text-cyan-400" },
      { route: "/dashboard/operations/homebase/collection-receipts", icon: "receipt_long", label: "Collection Receipts", desc: "View receipts", bg: "bg-emerald-50 dark:bg-emerald-900/20", iconColor: "text-emerald-600 dark:text-emerald-400" },
    ];

    const adminLinks = [
      { route: "/dashboard/administration/suppliers", icon: "store", label: "Suppliers", desc: "Manage suppliers", bg: "bg-primary-50 dark:bg-primary-900/20", iconColor: "text-primary-600 dark:text-primary-400" },
      { route: "/dashboard/administration/drivers", icon: "badge", label: "Drivers", desc: "Manage drivers", bg: "bg-secondary-50 dark:bg-secondary-900/20", iconColor: "text-secondary-600 dark:text-secondary-400" },
      { route: "/dashboard/administration/trucks", icon: "local_shipping", label: "Trucks", desc: "Manage fleet", bg: "bg-amber-50 dark:bg-amber-900/20", iconColor: "text-amber-600 dark:text-amber-400" },
      { route: "/dashboard/administration/inventory", icon: "warehouse", label: "Inventory", desc: "Container inventory", bg: "bg-violet-50 dark:bg-violet-900/20", iconColor: "text-violet-600 dark:text-violet-400" },
      { route: "/dashboard/administration/reports", icon: "assessment", label: "Reports", desc: "Generate reports", bg: "bg-red-50 dark:bg-red-900/20", iconColor: "text-red-600 dark:text-red-400" },
    ];

    const settingsLinks = [
      { route: "/dashboard/settings/containers", icon: "category", label: "Container Types", desc: "Configure container types", bg: "bg-primary-50 dark:bg-primary-900/20", iconColor: "text-primary-600 dark:text-primary-400" },
      { route: "/dashboard/settings/zones", icon: "map", label: "Zones", desc: "Manage zones", bg: "bg-secondary-50 dark:bg-secondary-900/20", iconColor: "text-secondary-600 dark:text-secondary-400" },
      { route: "/dashboard/settings/users", icon: "people", label: "Users", desc: "Manage users", bg: "bg-violet-50 dark:bg-violet-900/20", iconColor: "text-violet-600 dark:text-violet-400" },
    ];

    return {
      userName,
      stats,
      statsLoading,
      statsError,
      suppliersLoading,
      suppliersError,
      fetchStats,
      opsLinks,
      homebaseLinks,
      adminLinks,
      settingsLinks,
    };
  },
};
</script>

<style scoped>
.home-page > * {
  animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.home-page > *:nth-child(1) { animation-delay: 0s; }
.home-page > *:nth-child(2) { animation-delay: 0.05s; }
.home-page > *:nth-child(3) { animation-delay: 0.1s; }

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
